import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@bi-studio/shared/hooks';
import { FormEditView } from '@/components/forms';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { updateForm, getFolderForItem } from '@bi-studio/shared/apis';
import { AppLayout, LoadingScreen, ErrorState } from '@bi-studio/shared/components';
import { PageActions, FormEditActionsButton, PageContainer } from '@/components';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { 
    fetchFormDetails, 
    fetchListDataSources, 
    fetchTableSchema,
    selectCurrentForm, 
    selectFormLoading, 
    selectFormSources, 
    selectFormTables, 
    selectLoadingSources, 
    selectLoadingTables,
    resetFormState 
} from '@/store/slices/formSlice';
import type { FormInput } from '@bi-studio/shared/lib';

export default function EditFormPage() {
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id: formId } = useParams<{ id: string }>();

    const form = useAppSelector(selectCurrentForm);
    const isLoadingForm = useAppSelector(selectFormLoading);
    const dataSources = useAppSelector(selectFormSources);
    const tables = useAppSelector(selectFormTables);
    const loadingDataSources = useAppSelector(selectLoadingSources);
    const loadingTables = useAppSelector(selectLoadingTables);

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<string | null>("general");

    const mantineForm = useForm<FormInput>({
        initialValues: {
            name: "",
            description: "",
            dataSourceId: "",
            tableName: "",
            fields: [],
            allowedRoles: [],
            config: {
                isPublic: false,
                backgroundImage: "",
                backgroundColor: "",
                rateLimit: {
                    enabled: false,
                    limit: 5,
                    window: 60,
                }
            },
        },
        validate: {
            name: (value) => (value ? null : "Name is required"),
            dataSourceId: (value) => (value ? null : "Data source is required"),
            tableName: (value) => (value ? null : "Table is required"),
        },
    });

    const handleLogout = useCallback(() => {
        navigate("/login");
    }, [navigate]);

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            handleLogout();
        }
    }, [isAuthenticated, authLoading, handleLogout]);

    useEffect(() => {
        if (!token || !formId) return;

        dispatch(fetchFormDetails({ token, formId }))
            .unwrap()
            .then((fetchedForm) => {
                mantineForm.setValues({
                    name: fetchedForm.name,
                    description: fetchedForm.description || "",
                    dataSourceId: fetchedForm.dataSourceId,
                    tableName: fetchedForm.tableName,
                    fields: fetchedForm.fields || [],
                    allowedRoles: fetchedForm.allowedRoles || [],
                    config: fetchedForm.config || {},
                });
            })
            .catch((err) => {
                setError(err instanceof Error ? err.message : "Failed to load form details");
            });

        dispatch(fetchListDataSources(token));

        return () => {
            dispatch(resetFormState());
        };
    }, [token, formId, dispatch]);

    useEffect(() => {
        if (token && mantineForm.values.dataSourceId) {
            dispatch(fetchTableSchema({ token, dataSourceId: mantineForm.values.dataSourceId }));
        }
    }, [token, mantineForm.values.dataSourceId, dispatch]);

    const handleSave = async () => {
        const validation = mantineForm.validate();
        if (validation.hasErrors) {
            notifications.show({
                title: "Validation Error",
                message: "Please check the form for errors",
                color: "red",
            });
            return;
        }

        if (!token || !formId) return;

        try {
            setIsSaving(true);
            setError(null);

            await updateForm(token, formId, mantineForm.values);
            notifications.show({
                title: "Success",
                message: "Form updated successfully",
                color: "green",
            });
            navigate(`/forms/${formId}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update form");
        } finally {
            setIsSaving(false);
        }
    };

    const handleBack = useCallback(async () => {
        if (!token || !formId) {
            navigate(-1);
            return;
        }

        try {
            const folderInfo = await getFolderForItem(token, "form", formId);
            if (folderInfo.folderId) {
                navigate(`/folders/${folderInfo.folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (err) {
            console.error("Failed to get folder for form:", err);
            navigate(-1);
        }
    }, [token, formId, navigate]);

    const isReady = !!form;
    const isPageLoading = isLoadingForm || authLoading;

    if (!isReady && isPageLoading) {
        return (
            <AppLayout onLogout={handleLogout}>
                <LoadingScreen message="Loading form configuration..." />
            </AppLayout>
        );
    }

    if (error && !isReady) {
        return (
            <AppLayout onLogout={handleLogout}>
                <ErrorState 
                    title="Error Loading Form"
                    message={error}
                    onAction={() => window.location.reload()}
                    actionLabel="Retry"
                />
            </AppLayout>
        );
    }

    return (
        <AppLayout
            title="Workspace"
            subtitle="BI Studio"
            onLogout={handleLogout}
        >
            <PageContainer>
                <PageActions 
                    title={`Edit ${mantineForm.values.name || 'Form'}`}
                    onBack={handleBack}
                >
                    <FormEditActionsButton
                        handleCancel={() => navigate(`/forms/${formId}`)}
                        handleSave={handleSave}
                        isSaving={isSaving}
                    />
                </PageActions>

                <FormEditView
                    isSaving={isSaving}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    form={mantineForm}
                    dataSources={dataSources}
                    tables={tables}
                    loadingDataSources={loadingDataSources}
                    loadingTables={loadingTables}
                />
            </PageContainer>
        </AppLayout>
    );
}
